import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { logoutUser } from '../../store/slices/authSlice';
import { fetchUnreadCount } from '../../store/slices/notificationSlice';
import toast from 'react-hot-toast';

const FloatingSidebar = ({ isOpen, onClose }) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  
  // Get authentication state from Redux
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  
  // Get unread notification count from Redux
  const { unreadCount } = useAppSelector((state) => state.notifications);

  // Fetch unread count when sidebar opens
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      dispatch(fetchUnreadCount());
    }
  }, [isOpen, isAuthenticated, dispatch]);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.floating-sidebar') && !event.target.closest('#sidebar-toggle')) {
        onClose();
      }
    };

    if (isOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  // Check if a path is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if any profile submenu item is active
  const isProfileActive = () => {
    return location.pathname === '/jetsetter/profile/user-details' || 
           location.pathname === '/jetsetter/profile/change-password';
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      toast.success('Logged out successfully');
      navigate('/');
      onClose();
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 w-screen h-screen block top-0 overflow-hidden z-[998]"
          onClick={onClose}
        />
      )}

      {/* Floating Sidebar */}
      <nav
        className={`floating-sidebar fixed top-[13%] w-[280px] h-auto p-0 px-[1.3rem] shadow-[0px_2px_13px_2px_rgba(0,0,0,0.09)] bg-white z-[999] transition-all duration-500 ease-in-out ${
          isOpen ? 'right-0' : '-right-[300px]'
        }`}
      >
        {/* Menu Items */}
        <ul className="mt-6 mb-6 list-none p-0">
          
          {/* Profile Menu Item - Orange text with right arrow */}
          <li className="py-1 px-0 border-t border-[#e5e4f0]">
            <button
              onClick={toggleProfileMenu}
              className={`w-full text-left flex items-center justify-between bg-transparent font-normal text-[15px] cursor-pointer py-2 px-0 transition-all duration-300 ${
                isProfileMenuOpen 
                  ? 'text-[#ed7d31] border-4 border-[#ed7d31] rounded-md px-2' 
                  : 'text-[#ed7d31] border-4 border-transparent'
              }`}
            >
              <span>Profile</span>
              <i className={`fas fa-chevron-right text-sm text-[#ed7d31] transition-transform duration-300 ${
                isProfileMenuOpen ? 'rotate-90' : ''
              }`}></i>
            </button>

            {/* Profile Submenu with animation */}
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isProfileMenuOpen ? 'max-h-[200px] opacity-100 mt-2' : 'max-h-0 opacity-0'
            }`}>
              <div className="pl-4">
                <Link
                  to="/jetsetter/profile/user-details"
                  onClick={onClose}
                  className={`flex items-center gap-3 py-2 px-0 font-normal text-[14px] no-underline border-t border-b border-[#e5e4f0] hover:text-[#ed7d31] ${
                    isActive('/jetsetter/profile/user-details') 
                      ? 'text-[#ed7d31] border-l-4 border-l-[#ed7d31] pl-[10px]' 
                      : 'text-[#111236] border-l-0'
                  }`}
                >
                  <span>User Details</span>
                </Link>
                <Link
                  to="/jetsetter/profile/change-password"
                  onClick={onClose}
                  className={`flex items-center gap-3 py-2 px-0 font-normal text-[14px] no-underline hover:text-[#ed7d31] ${
                    isActive('/jetsetter/profile/change-password') 
                      ? 'text-[#ed7d31] border-l-4 border-l-[#ed7d31] pl-[10px]' 
                      : 'text-[#111236] border-l-0'
                  }`}
                >
                  <span>Change Password</span>
                </Link>
              </div>
            </div>
          </li>

          {/* Booking History - No Icon */}
          <li className={`py-[14px] px-0 border-t border-[#e5e4f0] ${
            isActive('/jetsetter/profile/booking-history') ? 'border-l-4 border-l-[#ed7d31] pl-[10px]' : 'border-l-0'
          }`}>
            <Link
              to="/jetsetter/profile/booking-history"
              onClick={onClose}
              className={`flex items-center gap-3 font-normal text-[15px] no-underline hover:text-[#ed7d31] ${
                isActive('/jetsetter/profile/booking-history') ? 'text-[#ed7d31]' : 'text-[#111236]'
              }`}
            >
              <span>Booking History</span>
            </Link>
          </li>

          {/* Messages - No Icon, with unread count on right, with bottom border */}
          <li className={`py-[14px] px-0 border-t border-b border-[#e5e4f0] ${
            isActive('/jetsetter/profile/messages') ? 'border-l-4 border-l-[#ed7d31] pl-[10px]' : 'border-l-0'
          }`}>
            <Link
              to="/jetsetter/profile/messages"
              onClick={onClose}
              className={`flex items-center justify-between font-normal text-[15px] no-underline hover:text-[#ed7d31] ${
                isActive('/jetsetter/profile/messages') ? 'text-[#ed7d31]' : 'text-[#111236]'
              }`}
            >
              <span>Messages</span>
              <span className="bg-red-500 text-white rounded-full px-2 py-0.5 text-xs font-semibold">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            </Link>
          </li>

          {/* Logout Button */}
          <li className="pt-5">
            <button
              onClick={handleLogout}
              className="block w-full h-10 px-3 py-1.5 text-[0.875rem] leading-7 font-bold text-black bg-[#ffd200] border border-[#ffd200] rounded-[0.3125rem] cursor-pointer min-w-[2.375rem] transition-all duration-150 ease-in-out hover:bg-[#ffd200] hover:border-[#ffd200] focus:bg-[#ffd200] focus:border-[#ffd200] disabled:bg-[#ffd200] disabled:border-[#ffd200]"
            >
              <i className='fas fa-power-off mr-2'></i>
              Logout
            </button>
          </li>
        </ul>
      </nav>
    </>
  );
};

export default FloatingSidebar;
